import { EVIDENCE_CONFIDENCE, scoreBasis } from './scoring.js';
/**
 * Missions (decision 0030): what a business sees instead of single controls. With the spread kept (decision
 * 0029), one control moves the score by only a few points, so each mission groups the controls behind one
 * plain goal, and its points are the gain of doing them together, from the real formula.
 *
 * Every control in the library belongs to exactly one mission. A mission shows the controls of the
 * assessment it applies to; one with none of them doesn't show.
 */
export const MISSION_VERSION = 'missions/v1';
/** How many open controls a mission's next steps take on, most points first. */
export const MISSION_STEPS = 3;
export const MISSIONS = [
    {
        id: 'accounts',
        name: 'Protect your accounts',
        blurb: 'Make sure a stolen password is not enough to get in.',
        controls: ['IA-*'],
    },
    {
        id: 'email',
        name: 'Stop email fraud',
        blurb: 'Fake invoices and CEO emails are the most common cyber loss for SMEs.',
        controls: ['EH-*'],
    },
    {
        id: 'ransomware',
        name: 'Be ransomware-ready',
        blurb: 'If attackers lock your files, can you be running again by Monday?',
        controls: ['BR-*', 'EP-12'],
    },
    {
        id: 'internet',
        name: 'Close the doors to the internet',
        blurb: 'Attackers scan for open and unpatched systems every day.',
        controls: ['NS-*', 'VM-*', 'AM-04'],
    },
    {
        id: 'devices',
        name: 'Look after your computers',
        blurb: 'Know every device you own and keep each one locked down.',
        controls: ['EP-*', 'AM-*'],
    },
    {
        id: 'data',
        name: 'Protect customer information',
        blurb: 'Customer and staff data, and what PDPA expects of you.',
        controls: ['DP-*'],
    },
    {
        id: 'watch',
        name: 'Spot attacks early',
        blurb: 'Most attacks leave traces days before the damage. Someone has to look.',
        controls: ['LD-*'],
    },
    {
        id: 'ready',
        name: 'Be ready for an attack',
        blurb: 'Know who to call and what to switch off in the first hour.',
        controls: ['IR-*'],
    },
    {
        id: 'suppliers',
        name: 'Keep suppliers in check',
        blurb: 'Your IT provider and vendors can open a door into your business.',
        controls: ['TP-*'],
    },
    {
        id: 'governance',
        name: 'Run security like a business',
        blurb: 'Someone in charge, written rules and a regular look at the risks.',
        controls: ['GV-*'],
    },
    // Industry packs: their own controls gathered in one mission, shown only where the pack applies.
    {
        id: 'factory',
        name: 'Secure the factory floor',
        blurb: 'Keep an office infection from stopping your production lines.',
        controls: ['NS-04', 'EP-14', 'BR-14', 'IR-08', 'AM-12', 'LD-12', 'TP-10', 'VM-08', 'VM-14'],
        pack: 'manufacturing',
    },
    {
        id: 'patients',
        name: 'Keep patient care running',
        blurb: 'Patient records stay private, devices stay safe, and care goes on when systems are down.',
        controls: [
            'AM-13', 'AM-16', 'BR-20', 'BR-21', 'DP-14', 'DP-15', 'DP-24', 'DP-25', 'DP-26', 'EH-19', 'EP-17',
            'GV-19', 'IA-22', 'IA-30', 'IA-31', 'IR-12', 'IR-18', 'LD-14', 'LD-18', 'NS-15', 'TP-16', 'VM-19',
        ], // prettier-ignore
        pack: 'healthcare',
    },
    {
        id: 'checkout',
        name: 'Secure your checkout',
        blurb: 'Card data and the tills and pages that take it.',
        controls: ['DP-16', 'DP-17', 'EP-15', 'LD-08', 'NS-14', 'VM-15'],
        pack: 'retail_ecommerce',
    },
    {
        id: 'fleet',
        name: 'Keep goods moving',
        blurb: 'Scanners, trucks and the partner systems your shipments depend on.',
        controls: ['EH-15', 'EP-08', 'NS-16', 'TP-08', 'TP-11'],
        pack: 'logistics',
    },
    {
        id: 'csa',
        name: 'Meet the Cyber Security Act',
        blurb: 'What the Act asks of an NCII entity: risk assessments and audits, the code of practice, six-hour notice.',
        controls: ['GV-20', 'GV-21', 'IR-20', 'IR-21'],
    },
    {
        id: 'regulator',
        name: 'Meet what your regulator expects',
        blurb: 'The controls Bank Negara’s RMiT asks of financial services firms.',
        controls: ['BR-16', 'DP-19', 'GV-14', 'IA-20', 'IA-21', 'IR-13', 'LD-13', 'TP-12'],
        pack: 'financial_services',
    },
    {
        id: 'rmit',
        name: 'Meet RMiT in full',
        blurb: 'What a licensed institution adds: a SOC, tested recovery in four hours, red teaming and cloud notice.',
        controls: [
            'BR-18', 'BR-19', 'DP-23', 'EH-18', 'GV-16', 'GV-17', 'GV-18', 'IA-28', 'IA-29',
            'IR-17', 'LD-16', 'LD-17', 'NS-21', 'TP-14', 'TP-15', 'VM-17', 'VM-18',
        ], // prettier-ignore
        pack: 'financial_services',
        profile: 'bnm_licensed',
    },
    {
        id: 'clients',
        name: 'Protect client matters',
        blurb: 'Client files, client money and the confidentiality you promised.',
        controls: ['BR-11', 'DP-18', 'EH-14', 'GV-12'],
        pack: 'professional_services',
    },
    {
        id: 'plant',
        name: 'Keep plants and pipelines safe',
        blurb: 'Control systems kept apart from the office, reached safely, and run by hand when they must.',
        controls: [
            'AM-14', 'AM-17', 'AM-18', 'BR-17', 'BR-22', 'BR-23', 'EP-18', 'EP-19', 'IA-23', 'IR-14', 'IR-19',
            'LD-19', 'NS-17', 'NS-22', 'NS-23', 'TP-17', 'VM-20',
        ], // prettier-ignore
        pack: 'energy',
    },
    {
        id: 'supply',
        name: 'Keep the supply flowing',
        blurb: 'Stations, meters and the consoles that run them, and a plan to restore supply.',
        controls: ['AM-15', 'DP-20', 'IA-24', 'IR-15', 'NS-18'],
        pack: 'utilities',
    },
    {
        id: 'network',
        name: 'Protect the network and its customers',
        blurb: 'Who manages the network, who can move a number, and who sees call records.',
        controls: ['DP-21', 'IA-25', 'LD-15', 'NS-19', 'TP-13'],
        pack: 'telecoms',
    },
    {
        id: 'public',
        name: 'Protect public services',
        blurb: 'Official information, officers’ access and the portals citizens rely on.',
        controls: ['EH-16', 'GV-15', 'IA-26', 'IR-16', 'VM-16'],
        pack: 'government',
    },
    {
        id: 'campus',
        name: 'Keep students and their records safe',
        blurb: 'Student records, shared computers and a campus network many people use.',
        controls: ['DP-22', 'EH-17', 'EP-16', 'IA-27', 'NS-20'],
        pack: 'education',
    },
];
/** The mission a control belongs to: named controls first, then its domain's wildcard. */
export function missionOf(control) {
    const named = MISSIONS.find((m) => m.controls.includes(control));
    if (named)
        return named;
    const domain = control.split('-')[0] + '-*';
    return MISSIONS.find((m) => m.controls.includes(domain)) ?? null;
}
/** The scoring helpers missions and plans share, over one stored trace. */
function planner(trace) {
    const now = scoreBasis(trace.basis).score;
    const byId = new Map(trace.controls.map((c) => [c.control, c]));
    const basisOf = new Map(trace.basis.controls.map((c) => [c.control, c]));
    const basisWith = (done) => scoreBasis({
        ...trace.basis,
        controls: trace.basis.controls.map((c) => done.has(c.control)
            ? { ...c, implementation: 1, confidence: Math.max(c.confidence, EVIDENCE_CONFIDENCE.USER_DECLARED) }
            : c),
    });
    const scoreWith = (done) => basisWith(new Set(done)).score;
    const residual = scoreBasis(trace.basis).residual;
    /** Residual risk each control removes, unrounded: orders steps worth the same points. */
    const gains = new Map();
    const gain = (id) => {
        if (!gains.has(id))
            gains.set(id, residual - basisWith(new Set([id])).residual);
        return gains.get(id);
    };
    const liftsCap = (id) => {
        const b = basisOf.get(id);
        return b.cap !== null && b.implementation === 0;
    };
    // Every failed kill switch, done together, lifts the caps. Other controls count on top of that: on
    // their own they would add nothing while a cap holds the score down.
    const lifters = trace.controls.filter((c) => liftsCap(c.control)).map((c) => c.control);
    const capped = lifters.length > 0;
    const base = capped ? scoreWith(lifters) : now;
    /** A control's points: on its own from today, or, while capped, on top of lifting the caps. */
    const worthOf = (c) => !capped || liftsCap(c.control) || (c.implementation ?? 0) >= 1 ? c.uplift : scoreWith([...lifters, c.control]) - base; // prettier-ignore
    const worth = new Map(trace.controls.map((c) => [c.control, worthOf(c)]));
    /** Cap lifters first, then the most points, the most risk removed, and the control id. */
    const rank = (a, b) => Number(liftsCap(b.control)) - Number(liftsCap(a.control)) ||
        worth.get(b.control) - worth.get(a.control) ||
        gain(b.control) - gain(a.control) ||
        (a.control < b.control ? -1 : 1);
    const step = (c) => ({
        control: c.control,
        name: byId.get(c.control).name,
        points: worth.get(c.control),
        lifts_cap: liftsCap(c.control),
    });
    const open = trace.controls.filter((c) => worth.get(c.control) > 0 || liftsCap(c.control));
    /** What a set of controls adds once the caps are lifted, on top of lifting them. */
    const afterCap = (ids) => (capped ? scoreWith([...lifters, ...ids]) - base : null);
    return {
        now,
        scoreWith,
        liftsCap,
        rank,
        step,
        open,
        isOpen: new Set(open.map((c) => c.control)),
        afterCap,
    };
}
/**
 * The missions of a scored assessment, from its stored trace, most points first. Points are computed
 * together because the formula isn't additive: a mission's points are not the sum of its steps', and two
 * missions together aren't the sum of both. Completing a control takes it to full implementation at its
 * current confidence or self-declared, as the trace's own uplift does.
 */
export function planMissions(trace) {
    const p = planner(trace);
    const plans = [];
    for (const m of MISSIONS) {
        const mine = trace.controls.filter((c) => missionOf(c.control)?.id === m.id);
        if (!mine.length)
            continue;
        const open = mine.filter((c) => p.isOpen.has(c.control));
        const steps = [...open].sort(p.rank).slice(0, MISSION_STEPS).map(p.step);
        plans.push({
            id: m.id,
            name: m.name,
            blurb: m.blurb,
            pack: m.pack ?? null,
            controls: mine.length,
            in_place: mine.filter((c) => c.status === 'in_place').length,
            points: open.length ? p.scoreWith(open.map((c) => c.control)) - p.now : 0,
            after_cap: p.afterCap(open.map((c) => c.control)),
            next: { steps, points: steps.length ? p.scoreWith(steps.map((s) => s.control)) - p.now : 0 },
            lifts_cap: open.some((c) => p.liftsCap(c.control)),
        });
    }
    return plans.sort((a, b) => b.points - a.points || (b.after_cap ?? 0) - (a.after_cap ?? 0) || MISSIONS.findIndex((x) => x.id === a.id) - MISSIONS.findIndex((x) => x.id === b.id)); // prettier-ignore
}
/** How many steps each phase of the 90-day plan takes on. */
export const PLAN_PHASE_STEPS = 4;
export const PLAN_PHASES = ['Next 30 days', '31–60 days', '61–90 days'];
/**
 * The business's next steps across every mission, ranked as a mission ranks its own (cap lifters first),
 * and split into a 90-day plan. Each phase's points count from the score after the phases before it, so
 * the phases add up to what they reach together. The first phase's first steps are the home page's
 * "next fixes".
 */
export function planActions(trace, perPhase = PLAN_PHASE_STEPS) {
    const p = planner(trace);
    const ranked = [...p.open]
        .sort(p.rank)
        .map((c) => ({ ...p.step(c), mission: missionOf(c.control)?.id ?? '' }));
    const phases = [];
    const done = [];
    let before = p.now;
    PLAN_PHASES.forEach((name, i) => {
        const steps = ranked.slice(i * perPhase, (i + 1) * perPhase);
        if (!steps.length)
            return;
        done.push(...steps.map((s) => s.control));
        const score = p.scoreWith(done);
        phases.push({ name, steps, points: score - before, score });
        before = score;
    });
    return {
        score: p.now,
        phases,
        all: {
            controls: ranked.length,
            points: ranked.length ? p.scoreWith(ranked.map((s) => s.control)) - p.now : 0,
        },
    };
}
/**
 * Each open control's points as a step: on its own from today, or, while a failed kill switch caps the
 * score, on top of lifting the caps. Controls with nothing left to add are left out.
 */
export function controlPoints(trace) {
    const p = planner(trace);
    return Object.fromEntries(p.open.map((c) => [c.control, p.step(c).points]));
}
/**
 * The target score planner (task 77): the fewest steps that take the score to `target`, with points
 * counted together from the real formula. The insurer's conditions (`must`) are always in the plan and
 * can't be set aside; while a failed kill switch caps the score, lifting it comes first. Then the step
 * that adds the most to the steps so far goes in, preferring missions already in the plan, until the
 * target is reached; a last pass drops any step the target doesn't need. `skip` is the what-if: steps
 * the business can't take on.
 */
export function planTarget(trace, target, opts = {}) {
    const p = planner(trace);
    const open = new Set(p.open.map((c) => c.control));
    const must = [...new Set(opts.must ?? [])].filter((c) => open.has(c));
    const skip = new Set((opts.skip ?? []).filter((c) => open.has(c) && !must.includes(c)));
    const usable = p.open.filter((c) => !skip.has(c.control)).map((c) => c.control);
    const max = p.scoreWith(usable);
    const start = new Set(must);
    if (target > p.now)
        for (const c of usable)
            if (p.liftsCap(c))
                start.add(c);
    const candidates = p.open.filter((c) => !skip.has(c.control));
    /** Drops what the target doesn't need, the steps adding least first. Conditions stay. */
    const prune = (chosen) => {
        let reached = p.scoreWith(chosen);
        if (reached < target)
            return { chosen, reached };
        const loose = [...chosen]
            .filter((c) => !must.includes(c))
            .map((c) => ({ c, without: p.scoreWith([...chosen].filter((x) => x !== c)) }))
            .sort((a, b) => b.without - a.without);
        for (const { c } of loose) {
            const without = p.scoreWith([...chosen].filter((x) => x !== c));
            if (without >= target) {
                chosen.delete(c);
                reached = without;
            }
        }
        return { chosen, reached };
    };
    // Two ways in: the step adding most to the steps so far (preferring missions already in the plan), and
    // the 90-day plan's order. Neither is always shortest, so the shorter wins.
    const greedy = () => {
        const chosen = new Set(start);
        let reached = p.scoreWith(chosen);
        while (reached < target) {
            const inPlan = new Set([...chosen].map((c) => missionOf(c)?.id));
            let best = null;
            for (const c of candidates) {
                if (chosen.has(c.control))
                    continue;
                const score = p.scoreWith([...chosen, c.control]);
                const same = inPlan.has(missionOf(c.control)?.id);
                if (!best || score > best.score || (score === best.score && same && !best.same))
                    best = { control: c.control, score, same }; // prettier-ignore
            }
            if (!best || best.score <= reached)
                break;
            chosen.add(best.control);
            reached = best.score;
        }
        return prune(chosen);
    };
    const ranked = () => {
        const chosen = new Set(start);
        for (const c of [...candidates].sort(p.rank)) {
            if (p.scoreWith(chosen) >= target)
                break;
            chosen.add(c.control);
        }
        return prune(chosen);
    };
    const spread = (x) => new Set([...x].map((c) => missionOf(c)?.id)).size;
    const { chosen, reached } = [greedy(), ranked()].sort((a, b) => Number(b.reached >= target) - Number(a.reached >= target) ||
        a.chosen.size - b.chosen.size ||
        spread(a.chosen) - spread(b.chosen) ||
        b.reached - a.reached)[0];
    const steps = p.open
        .filter((c) => chosen.has(c.control))
        .sort(p.rank)
        .map((c) => ({
        ...p.step(c),
        mission: missionOf(c.control)?.id ?? '',
        condition: must.includes(c.control),
    }));
    const missions = new Map();
    for (const s of steps) {
        const m = missionOf(s.control);
        if (!m)
            continue;
        const row = missions.get(m.id) ?? { id: m.id, name: m.name, steps: 0 };
        row.steps++;
        missions.set(m.id, row);
    }
    return {
        target,
        score: p.now,
        reaches: reached,
        reachable: reached >= target,
        max,
        steps,
        missions: [...missions.values()].sort((a, b) => b.steps - a.steps),
        skipped: [...skip],
    };
}
/** The first `n` steps of the plan and what they add together. */
export function nextFixes(trace, n = MISSION_STEPS) {
    const plan = planActions(trace, n);
    const first = plan.phases[0];
    return { steps: first?.steps ?? [], points: first?.points ?? 0, score: first?.score ?? plan.score };
}
//# sourceMappingURL=missions.js.map